import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createGrade,
  deleteSchoolGradeRepresentative,
  deleteGrade,
  getGrades,
  getOrphanGradeIds,
  getSchoolGradeRepresentatives,
  purgeOrphanGradeData,
  setSchoolGradeRepresentative,
  updateGradeName,
} from '../services/gradeTrackService';
import { getCurrentUserDisplay } from '../services/authService';
import { Grade } from '../types/domain';
import { parseGradeDisplayName, CLASS_DISPLAY_NAME_MAX_LENGTH, clampClassDisplayName } from '../utils/gradeDisplayName';
import { usePaidPassGate } from '../contexts/PaidPassGateContext';
import { usePlanGateRows } from '../hooks/usePlanGateRows';
import {
  PLUS_PASS_MAX_CLASSES,
} from '../utils/subscriptionPassStorage';
import { maskEmailForDisplay } from '../utils/loginEmailMask';
import { supabase } from '../supabaseClient';
import {
  academicYearGraceEnd,
  academicYearShortLabel,
  classYearStatus,
  currentAcademicYear,
  formatYmd,
  isAcademicYearLocked,
} from '../utils/classAcademicYear';
import { fetchMyPassYears, getSelectedAcademicYear } from '../utils/selectedAcademicYear';
import { listMyYearBundles, type AcademicYearBundle } from '../services/academicYearBundleService';
import { buildClassArchive, buildYearGeneralArchive, triggerBrowserDownload } from '../services/classArchiveExportService';

function parseClassName(raw: string): { title: string; grades: number[] } {
  const parsed = parseGradeDisplayName(raw);
  return { title: parsed.title || raw, grades: parsed.grades };
}

const GRADE_NUMBERS = [1, 2, 3, 4, 5, 6] as const;

export type ClassSettingsDataChangeOptions = {
  /** 신규 클래스 등록 직후 상단 내비에서 해당 클래스를 선택 */
  selectGradeId?: string;
};

interface ClassSettingsSectionProps {
  workspaceId: string;
  onDataChange: (options?: ClassSettingsDataChangeOptions) => void;
  /** 모달 헤더용: 생성한 클래스 수 / 플랜 상한 */
  onClassStatsChange?: (stats: { count: number; max: number }) => void;
}

type DeleteTarget = {
  gradeId: string;
  classLabel: string;
  orphan: boolean;
};

const ClassSettingsSection: React.FC<ClassSettingsSectionProps> = ({
  workspaceId,
  onDataChange,
  onClassStatsChange,
}) => {
  const { isFreePass, maxClasses, requirePaidPass } = usePaidPassGate();
  /** 안내 문구용 PLUS 클래스 상한(관리자 「이용권별 권한 관리」 PLUS 열 값) */
  const plusMaxClasses = usePlanGateRows()?.individual?.maxClasses ?? PLUS_PASS_MAX_CLASSES;
  const [grades, setGrades] = useState<Grade[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [classModalOpen, setClassModalOpen] = useState(false);
  const [classModalName, setClassModalName] = useState('');
  const [classModalGrades, setClassModalGrades] = useState<number[]>([]);
  const [editingClassId, setEditingClassId] = useState<string | null>(null);
  const [classModalRepresentativeIntent, setClassModalRepresentativeIntent] = useState(false);
  const [representativeByGrade, setRepresentativeByGrade] = useState<Record<number, string>>({});
  const [orphanGradeIds, setOrphanGradeIds] = useState<string[]>([]);
  const [deleteTargetClass, setDeleteTargetClass] = useState<DeleteTarget | null>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [passYears, setPassYears] = useState<number[]>([]);
  useEffect(() => {
    let alive = true;
    void fetchMyPassYears().then((ys) => {
      if (alive) setPassYears(ys);
    });
    return () => {
      alive = false;
    };
  }, []);
  const [classModalYear, setClassModalYear] = useState<number>(() => {
    const y = getSelectedAcademicYear();
    return isAcademicYearLocked(y) ? currentAcademicYear() : y;
  });
  const [archiveTarget, setArchiveTarget] = useState<Grade | null>(null);
  const [archiveBundle, setArchiveBundle] = useState<AcademicYearBundle | null>(null);
  const [yearBundles, setYearBundles] = useState<AcademicYearBundle[]>([]);
  useEffect(() => {
    let alive = true;
    void listMyYearBundles().then((rows) => {
      if (alive) setYearBundles(rows);
    });
    return () => {
      alive = false;
    };
  }, []);
  const archiveYear = archiveBundle ? archiveBundle.academic_year : null;
  const [archivePassword, setArchivePassword] = useState('');
  const [archivePassword2, setArchivePassword2] = useState('');
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveStatus, setArchiveStatus] = useState('');

  // FREE: 기록 중지(보관 기간) 자료는 내려받기 허용, 진행 중인 학년도 자료만 PLUS 필요
  const archiveNeedsPaid = (year: number | null | undefined) =>
    isFreePass && classYearStatus(year) !== 'locked';

  const openArchiveModal = (g: Grade) => {
    if (archiveNeedsPaid(g.academic_year)) {
      requirePaidPass();
      return;
    }
    setArchiveTarget(g);
    setArchiveBundle(null);
    setArchivePassword('');
    setArchivePassword2('');
    setArchiveStatus('');
  };

  const openYearArchiveModal = (bundle: AcademicYearBundle) => {
    if (archiveNeedsPaid(bundle.academic_year)) {
      requirePaidPass();
      return;
    }
    setArchiveTarget(null);
    setArchiveBundle(bundle);
    setArchivePassword('');
    setArchivePassword2('');
    setArchiveStatus('');
  };
  const closeArchiveModal = () => {
    setArchiveTarget(null);
    setArchiveBundle(null);
  };

  const handleArchiveDownload = async () => {
    if ((!archiveTarget && archiveYear == null) || archiveBusy) return;
    if (archiveNeedsPaid(archiveTarget ? archiveTarget.academic_year : archiveYear)) {
      requirePaidPass();
      return;
    }
    if (archivePassword !== archivePassword2) {
      setArchiveStatus('비밀번호가 서로 다릅니다.');
      return;
    }
    setArchiveBusy(true);
    setArchiveStatus('내려받을 자료를 모으는 중…');
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id ?? workspaceId;
      const onProgress = (p: { step: string }) => setArchiveStatus(`자료를 모으는 중… (${p.step})`);
      const result = archiveTarget
        ? await buildClassArchive({
            workspaceId,
            userId,
            grade: archiveTarget,
            password: archivePassword || undefined,
            onProgress,
          })
        : await buildYearGeneralArchive({
            userId,
            bundleId: (archiveBundle as AcademicYearBundle).id,
            year: archiveYear as number,
            password: archivePassword || undefined,
            onProgress,
          });
      triggerBrowserDownload(result.blob, result.fileName);
      const mb = (result.bytes / 1048576).toFixed(1);
      setArchiveStatus(
        `내려받기 완료: ${result.fileName} (${mb}MB, 첨부 ${result.fileCount}개)` +
          (result.warnings.length ? ` · 일부 항목 ${result.warnings.length}건은 넣지 못했습니다.` : '')
      );
    } catch (e: unknown) {
      setArchiveStatus(e instanceof Error ? e.message : '내려받기에 실패했습니다.');
    } finally {
      setArchiveBusy(false);
    }
  };

  const classesByGrade = useMemo(() => {
    const map: Record<number, Grade[]> = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    grades.forEach((g) => {
      const parsed = parseClassName(g.name);
      parsed.grades.forEach((num) => {
        if (num >= 1 && num <= 6) map[num].push(g);
      });
    });
    GRADE_NUMBERS.forEach((num) => {
      map[num].sort((a, b) => {
        const pa = parseClassName(a.name);
        const pb = parseClassName(b.name);
        return pa.title.localeCompare(pb.title, 'ko');
      });
    });
    return map;
  }, [grades]);

  const loadClasses = useCallback(async () => {
    setInitialLoading(true);
    try {
      // 오픈 경로: 목록 표시에 필요한 grades·대표만 먼저 로드 (고아 스캔·대표 DB 동기화는 백그라운드)
      const [gradeRows, representativeRows] = await Promise.all([
        getGrades(workspaceId),
        getSchoolGradeRepresentatives(workspaceId),
      ]);

      const gradeByNumber: Record<number, Grade[]> = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
      gradeRows.forEach((g) => {
        parseClassName(g.name).grades.forEach((num) => {
          if (num >= 1 && num <= 6) gradeByNumber[num].push(g);
        });
      });
      const nextRepresentativeByGrade: Record<number, string> = {};
      representativeRows.forEach((row) => {
        const gradeNumber = Number(row.grade_number);
        if (gradeNumber < 1 || gradeNumber > 6) return;
        nextRepresentativeByGrade[gradeNumber] = row.grade_id;
      });
      const upsertTasks: Array<Promise<unknown>> = [];
      const deleteTasks: Array<Promise<unknown>> = [];
      GRADE_NUMBERS.forEach((num) => {
        const classes = gradeByNumber[num];
        if (!classes.length) {
          if (nextRepresentativeByGrade[num]) {
            deleteTasks.push(deleteSchoolGradeRepresentative(workspaceId, num));
            delete nextRepresentativeByGrade[num];
          }
          return;
        }
        const currentRep = nextRepresentativeByGrade[num];
        const hasCurrent = currentRep && classes.some((c) => c.id === currentRep);
        if (!hasCurrent) {
          const fallback = classes[0]!.id;
          nextRepresentativeByGrade[num] = fallback;
          upsertTasks.push(setSchoolGradeRepresentative(workspaceId, num, fallback));
        }
      });

      setGrades(gradeRows);
      setRepresentativeByGrade(nextRepresentativeByGrade);

      if (upsertTasks.length > 0 || deleteTasks.length > 0) {
        void Promise.all([...upsertTasks, ...deleteTasks]).catch(() => {
          // school_grade_representatives 미적용·네트워크 등: DB 동기화 없이 화면용 대표만 유지
        });
      }

      void getOrphanGradeIds(workspaceId)
        .then((orphanIds) => setOrphanGradeIds(orphanIds))
        .catch(() => {
          // 고아 목록은 삭제 UI용 — 실패 시 기존 목록 유지
        });
    } finally {
      setInitialLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    void loadClasses();
  }, [workspaceId, loadClasses]);

  useEffect(() => {
    if (!deleteTargetClass || !workspaceId || accountEmail) return;
    let cancelled = false;
    void (async () => {
      const display = await getCurrentUserDisplay(workspaceId, '');
      if (cancelled) return;
      setAccountEmail((display?.email ?? '').trim());
    })();
    return () => {
      cancelled = true;
    };
  }, [deleteTargetClass, workspaceId, accountEmail]);

  const encodeClassName = (name: string, grades: number[]) => {
    const base = clampClassDisplayName(name);
    const normalizedGrades = Array.from(new Set(grades)).sort((a, b) => a - b);
    if (normalizedGrades.length === 0) return base;
    return `${base}::${normalizedGrades.join(',')}`;
  };

  const atClassLimit = grades.length >= maxClasses;

  useEffect(() => {
    onClassStatsChange?.({ count: grades.length, max: maxClasses });
  }, [grades.length, maxClasses, onClassStatsChange]);

  const toggleClassModalGrade = (n: number) => {
    setClassModalGrades((prev) =>
      prev.includes(n) ? prev.filter((g) => g !== n) : [...prev, n].sort((a, b) => a - b)
    );
  };

  const handleSaveClass = async () => {
    const trimmedName = classModalName.trim();
    if (!trimmedName || classModalGrades.length === 0) return;
    if (trimmedName.length > CLASS_DISPLAY_NAME_MAX_LENGTH) {
      setMessage(`클래스 이름은 ${CLASS_DISPLAY_NAME_MAX_LENGTH}글자까지 입력할 수 있습니다.`);
      return;
    }
    if (!editingClassId && atClassLimit) {
      if (isFreePass) {
        requirePaidPass();
        return;
      }
      setMessage(`PLUS 이용권은 클래스를 ${maxClasses}개까지 등록할 수 있습니다.`);
      return;
    }
    setLoading(true);
    setMessage('');
    const wasCreate = !editingClassId;
    try {
      const encoded = encodeClassName(trimmedName, classModalGrades);
      let savedGradeId = editingClassId;
      if (editingClassId) {
        await updateGradeName(editingClassId, encoded);
      } else {
        const yearForCreate =
          classModalYear > currentAcademicYear() && !passYears.includes(classModalYear) ? currentAcademicYear() : classModalYear;
        const created = await createGrade(workspaceId, { name: encoded, sortOrder: 1, academicYear: yearForCreate });
        savedGradeId = created.id;
      }
      if (classModalRepresentativeIntent && savedGradeId) {
        for (const n of classModalGrades) {
          await setSchoolGradeRepresentative(workspaceId, n, savedGradeId);
        }
      }
      await loadClasses();
      onDataChange(
        wasCreate && savedGradeId ? { selectGradeId: savedGradeId } : undefined
      );
      setMessage(
        classModalRepresentativeIntent
          ? '클래스를 저장하고 학년 대표로 설정했습니다.'
          : '클래스가 저장되었습니다.'
      );
      setClassModalOpen(false);
      setClassModalName('');
      setClassModalGrades([]);
      setEditingClassId(null);
      setClassModalRepresentativeIntent(false);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '저장에 실패했습니다.';
      setMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const modalClassIsRepresentativeForAllGrades =
    Boolean(editingClassId) &&
    classModalGrades.length > 0 &&
    classModalGrades.every((n) => representativeByGrade[n] === editingClassId);

  const handleOpenDeleteModal = (target: DeleteTarget) => {
    setDeleteTargetClass(target);
    setDeleteConfirmEmail('');
    setDeleteError('');
  };

  const closeDeleteModal = (force = false) => {
    if (deleteBusy && !force) return;
    setDeleteTargetClass(null);
    setDeleteConfirmEmail('');
    setDeleteError('');
  };

  const renderOrphanGradeBlock = () => {
    if (initialLoading || orphanGradeIds.length === 0) return null;
    return (
      <div className="team-create-section cp-class-orphan-section">
        <p className="page-desc">
          고아 클래스 데이터 ({orphanGradeIds.length}) — 클래스 목록에는 없지만 학사일정·편제 등에 남아 있는 항목
        </p>
        <div className="cp-profile-class-grade-blocks">
          {orphanGradeIds.map((gradeId) => (
            <div key={`orphan-${gradeId}`} className="cp-profile-class-setting-block">
              <div className="cp-profile-class-setting-card-main">
                <span className="cp-profile-class-setting-name">고아 클래스</span>
                <span className="cp-profile-class-setting-id" title={gradeId}>
                  {gradeId.slice(0, 8)}…
                </span>
              </div>
              <div className="cp-class-block-actions cp-class-block-actions-inline">
                <button
                  type="button"
                  className="track-icon-button"
                  onClick={() =>
                    handleOpenDeleteModal({
                      gradeId,
                      classLabel: `고아 클래스 ${gradeId.slice(0, 8)}`,
                      orphan: true,
                    })
                  }
                >
                  <img src="/Trash.svg" alt="고아 클래스 삭제" className="track-icon" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleConfirmDeleteClass = async () => {
    if (!deleteTargetClass) return;
    if (!accountEmail) {
      setDeleteError('이 계정에는 이메일 정보가 없어 삭제 확인을 진행할 수 없습니다.');
      return;
    }
    if (deleteConfirmEmail.trim().toLowerCase() !== accountEmail.trim().toLowerCase()) {
      setDeleteError('계정 이메일이 일치하지 않습니다.');
      return;
    }
    setDeleteBusy(true);
    setDeleteError('');
    setMessage('');
    try {
      if (deleteTargetClass.orphan) {
        await purgeOrphanGradeData(workspaceId, deleteTargetClass.gradeId);
      } else {
        await deleteGrade(deleteTargetClass.gradeId);
      }
      await loadClasses();
      onDataChange();
      setMessage(
        deleteTargetClass.orphan
          ? '고아 클래스 데이터를 삭제했습니다.'
          : '클래스를 삭제했습니다. 해당 클래스의 연결/저장 데이터도 함께 삭제되었습니다.'
      );
      closeDeleteModal(true);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : '클래스 삭제에 실패했습니다.');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <>
      {message && <div className="inline-message">{message}</div>}

      <article className="cp-card cp-class-settings-card">
        <h3>클래스 설정</h3>
        {initialLoading ? (
          <p className="team-grid-placeholder">클래스 정보를 불러오는 중...</p>
        ) : (
          <>
            <div className="cp-class-buttons-row">
              <button
                type="button"
                className={`primary-row-button cp-compact-btn${
                  atClassLimit ? ' is-pass-locked' : ''
                }`}
                onClick={() => {
                  if (atClassLimit) {
                    if (isFreePass) {
                      requirePaidPass();
                      return;
                    }
                    setMessage(
                      `PLUS 이용권은 클래스를 ${maxClasses}개까지 등록할 수 있습니다.`
                    );
                    return;
                  }
                  setEditingClassId(null);
                  setClassModalName('');
                  setClassModalGrades([]);
                  setClassModalRepresentativeIntent(false);
                  setClassModalYear(currentAcademicYear());
                  setClassModalOpen(true);
                }}
                disabled={loading}
                title={
                  atClassLimit
                    ? isFreePass
                      ? `프리 이용권은 클래스를 ${maxClasses}개만 등록할 수 있습니다. 이용권을 구매하면 최대 ${plusMaxClasses}개까지 등록할 수 있습니다.`
                      : `PLUS 이용권은 클래스를 ${maxClasses}개까지 등록할 수 있습니다.`
                    : undefined
                }
                aria-disabled={atClassLimit || undefined}
              >
                클래스 등록하기
              </button>
            </div>
            <div className="cp-profile-class-grade-grid">
              {GRADE_NUMBERS.map((gradeNumber) => {
                const gradeClasses = classesByGrade[gradeNumber];
                return (
                  <section key={`profile-grade-${gradeNumber}`} className="cp-profile-class-grade-column">
                    <h4 className="cp-profile-class-grade-title">{gradeNumber}학년</h4>
                    {gradeClasses.length === 0 ? (
                      <p className="cp-profile-class-grade-empty">등록된 클래스가 없습니다.</p>
                    ) : (
                      <div className="cp-profile-class-grade-blocks">
                        {gradeClasses.map((g) => {
                          const parsed = parseClassName(g.name);
                          const isRepresentative = representativeByGrade[gradeNumber] === g.id;
                          return (
                            <div
                              key={`${gradeNumber}-${g.id}`}
                              className="cp-profile-class-setting-block"
                            >
                              <div className="cp-profile-class-setting-card-main">
                                {isRepresentative ? (
                                  <span className="cp-class-representative-badge">대표</span>
                                ) : null}
                                {g.academic_year ? (
                                  <span
                                    className={`cp-class-year-tag${
                                      classYearStatus(g.academic_year) !== 'active' ? ' is-locked' : ''
                                    }`}
                                  >
                                    {academicYearShortLabel(g.academic_year)}
                                  </span>
                                ) : null}
                                <span className="cp-profile-class-setting-name">{parsed.title}</span>
                                {g.academic_year && classYearStatus(g.academic_year) === 'locked' ? (
                                  <span className="cp-class-lock-note">
                                    기록 중지 · {formatYmd(academicYearGraceEnd(g.academic_year))}까지 내려받기 가능
                                  </span>
                                ) : null}
                                {g.academic_year && classYearStatus(g.academic_year) === 'expired' ? (
                                  <span className="cp-class-lock-note">보관 기간 종료 · 삭제 예정</span>
                                ) : null}
                                {parsed.grades.length > 0 ? (
                                  <span className="cp-profile-class-setting-chips">
                                    {parsed.grades.map((num) => (
                                      <span key={num} className="class-grade-chip-display">
                                        {num}
                                      </span>
                                    ))}
                                  </span>
                                ) : null}
                              </div>
                              <div className="cp-class-block-actions cp-class-block-actions-inline">
                                <button
                                  type="button"
                                  className="track-icon-button"
                                  title={archiveNeedsPaid(g.academic_year) ? '진행 중인 학년도 내려받기는 PLUS 이용권에서 할 수 있습니다.' : '클래스 자료 내려받기'}
                                  aria-label="클래스 자료 내려받기"
                                  onClick={() => openArchiveModal(g)}
                                >
                                  <img src="/Download.svg" alt="" className="track-icon" />
                                </button>
                                <button
                                  type="button"
                                  className="track-icon-button"
                                  hidden={classYearStatus(g.academic_year) !== 'active'}
                                  onClick={() => {
                                    const current = parseClassName(g.name);
                                    setEditingClassId(g.id);
                                    setClassModalName(clampClassDisplayName(current.title));
                                    setClassModalGrades(current.grades.length ? current.grades : [1]);
                                    setClassModalRepresentativeIntent(false);
                                    setClassModalOpen(true);
                                  }}
                                >
                                  <img src="/edit.svg" alt="클래스 수정" className="track-icon" />
                                </button>
                                <button
                                  type="button"
                                  className="track-icon-button"
                                  onClick={() =>
                                    handleOpenDeleteModal({
                                      gradeId: g.id,
                                      classLabel: parseClassName(g.name).title,
                                      orphan: false,
                                    })
                                  }
                                >
                                  <img src="/Trash.svg" alt="클래스 삭제" className="track-icon" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
            {renderOrphanGradeBlock()}
          </>
        )}
        <div className="cp-year-general-archive">
          <p className="page-desc cp-year-general-archive-desc">
            학년도 일반 자료(일일기록·할일노트·예산노트)는 클래스와 따로 학년도별로 내려받습니다.
          </p>
          <div className="cp-year-general-archive-actions">
            {yearBundles.length === 0 ? <span className="page-desc">아직 학년도 자료가 없습니다.</span> : null}
            {yearBundles
              .filter((b) => classYearStatus(b.academic_year) !== 'expired')
              .map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className="secondary-row-button"
                  title={
                    archiveNeedsPaid(b.academic_year)
                      ? '진행 중인 학년도 내려받기는 PLUS 이용권에서 할 수 있습니다.'
                      : undefined
                  }
                  onClick={() => openYearArchiveModal(b)}
                >
                  {b.academic_year}학년도 일반 자료(데일리노트, TODO노트, 예산노트) 내려받기
                  {classYearStatus(b.academic_year) === 'locked' ? ` (기록 중지 · ${b.grace_ends_on}까지)` : ''}
                </button>
              ))}
          </div>
        </div>
      </article>

      {classModalOpen && (
        <div
          className="cp-modal-backdrop"
          style={{ zIndex: 2700 }}
          onClick={() => {
            setClassModalOpen(false);
            setClassModalName('');
            setClassModalGrades([]);
            setEditingClassId(null);
            setClassModalRepresentativeIntent(false);
          }}
        >
          <div className="cp-modal cp-class-register-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingClassId ? '클래스 수정하기' : '클래스 등록하기'}</h3>
            <div className="team-create-section">
              <p className="page-desc">
                클래스 이름 (최대 {CLASS_DISPLAY_NAME_MAX_LENGTH}글자)
              </p>
              <div className="form-row">
                <input
                  value={classModalName}
                  onChange={(e) =>
                    setClassModalName(e.target.value.slice(0, CLASS_DISPLAY_NAME_MAX_LENGTH))
                  }
                  maxLength={CLASS_DISPLAY_NAME_MAX_LENGTH}
                  placeholder="예: 프로젝트반"
                />
              </div>
            </div>
            {!editingClassId ? (
              <div className="team-create-section">
                <p className="page-desc">학년도</p>
                <div className="class-grade-chips">
                  {[currentAcademicYear(), currentAcademicYear() + 1].map((y) => {
                    const needsPass = y > currentAcademicYear() && !passYears.includes(y);
                    return (
                      <button
                        key={y}
                        type="button"
                        className={'class-grade-chip cp-class-year-chip' + (classModalYear === y ? ' selected' : '')}
                        onClick={() => setClassModalYear(y)}
                        disabled={needsPass}
                        title={needsPass ? `${y}학년도 이용권이 있어야 만들 수 있습니다.` : undefined}
                      >
                        {y}학년도
                      </button>
                    );
                  })}
                </div>
                {!passYears.includes(currentAcademicYear() + 1) ? (
                  <p className="page-desc cp-class-year-help">
                    {currentAcademicYear() + 1}학년도 클래스는 {currentAcademicYear() + 1}학년도 이용권을 구매하면 만들 수 있습니다.
                  </p>
                ) : null}
                <p className="page-desc cp-class-year-help">
                  클래스는 학년도마다 새로 만듭니다. 학년도가 끝나면(다음 해 2월 말일) 기록이 중지되고,
                  3개월 동안 내려받기만 할 수 있으며 그 뒤 서버에서 삭제됩니다.
                </p>
              </div>
            ) : null}
            <div className="team-create-section">
              <p className="page-desc">학년 선택 (1-6학년)</p>
              <div className="class-grade-chips">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={'class-grade-chip' + (classModalGrades.includes(n) ? ' selected' : '')}
                    onClick={() => toggleClassModalGrade(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="team-create-section cp-class-modal-rep-section">
              <p className="page-desc">대표 클래스</p>
              <div className="cp-class-modal-rep-actions">
                {classModalGrades.length === 0 ? (
                  <p className="page-desc cp-class-modal-rep-hint">학년을 먼저 선택해 주세요.</p>
                ) : modalClassIsRepresentativeForAllGrades ? (
                  <span className="cp-class-modal-rep-indicator" aria-label="선택한 학년의 대표 클래스입니다">
                    대표 클래스
                  </span>
                ) : (
                  <button
                    type="button"
                    className="cp-class-modal-rep-btn"
                    disabled={loading || classModalRepresentativeIntent}
                    onClick={() => setClassModalRepresentativeIntent(true)}
                  >
                    {classModalRepresentativeIntent ? '저장 시 대표로 설정됩니다' : '대표로 설정하기'}
                  </button>
                )}
              </div>
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => {
                  setClassModalOpen(false);
                  setClassModalName('');
                  setClassModalGrades([]);
                  setEditingClassId(null);
                  setClassModalRepresentativeIntent(false);
                }}
              >
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={() => void handleSaveClass()}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteTargetClass ? (
        <div className="cp-modal-backdrop" style={{ zIndex: 2800 }} onClick={() => closeDeleteModal()}>
          <div className="cp-modal cp-profile-withdraw-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="cp-profile-withdraw-title">클래스 삭제</h3>
            <p className="cp-profile-withdraw-desc">
              <strong>{deleteTargetClass.classLabel}</strong> 삭제를 진행합니다. 삭제 시 해당 클래스가 연결된
              학사일정·수업시간·요일별 기준 수업시수 연결이 모두 해제되고,
              클래스 하위 저장 데이터가 함께 삭제됩니다. 복구할 수 없습니다.
              계속하려면 <strong>계정 이메일</strong>을 아래에 입력해 주세요.
            </p>
            <div className="cp-profile-withdraw-field">
              <label className="cp-profile-withdraw-label" htmlFor="cp-class-delete-account-email">
                계정 이메일 확인
              </label>
              <input
                id="cp-class-delete-account-email"
                type="email"
                autoComplete="off"
                className="cp-profile-withdraw-input"
                placeholder={accountEmail ? maskEmailForDisplay(accountEmail) : '계정 이메일'}
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                disabled={deleteBusy}
              />
            </div>
            {deleteError ? (
              <p className="cp-profile-withdraw-error" role="alert">
                {deleteError}
              </p>
            ) : null}
            <div className="cp-modal-footer cp-leave-modal-actions cp-profile-withdraw-footer">
              <button type="button" className="cp-btn-secondary" disabled={deleteBusy} onClick={() => closeDeleteModal()}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                style={{ background: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                disabled={
                  deleteBusy ||
                  !accountEmail.trim() ||
                  deleteConfirmEmail.trim().toLowerCase() !== accountEmail.trim().toLowerCase()
                }
                onClick={() => void handleConfirmDeleteClass()}
              >
                {deleteBusy ? '삭제 중…' : '클래스 삭제 진행'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {archiveTarget || archiveYear != null ? (
        <div
          className="cp-modal-backdrop"
          style={{ zIndex: 2700 }}
          onClick={() => {
            if (!archiveBusy) closeArchiveModal();
          }}
        >
          <div className="cp-modal cp-class-archive-modal" onClick={(e) => e.stopPropagation()}>
            {archiveTarget ? (
              <>
                <h3>
                  {archiveTarget.academic_year ? `${archiveTarget.academic_year}학년도 ` : ''}
                  {parseClassName(archiveTarget.name).title} 내려받기
                </h3>
                <p className="page-desc">아래 자료를 파일 하나(zip)로 내려받습니다. 학생 개인정보가 들어 있으니 안전하게 보관해 주세요.</p>
                <ul className="cp-class-archive-contents page-desc">
                  <li>편제·기준 시수</li>
                  <li>연간시간표·기초시간표·일일시간표</li>
                  <li>프로그램 학습내용·진도</li>
                  <li>연간 프로젝트·주간학습안내</li>
                  <li>행동특성·범교과 계획</li>
                  <li>과제노트·포인트노트</li>
                  <li>학생 명단·그룹·포인트·코인·메모</li>
                  <li>평가계획·소통·과제 챌린지</li>
                  <li>학사일정·첨부 이미지·공유 자료 사본</li>
                </ul>
              </>
            ) : (
              <>
                <h3>{archiveYear}학년도 일반 자료 내려받기</h3>
                <p className="page-desc">
                  {archiveYear}학년도 자료 묶음을 파일 하나(zip)로 내려받습니다.
                </p>
                <ul className="cp-class-archive-contents page-desc">
                  <li>데일리노트(일일기록)</li>
                  <li>TODO노트(할일·반복 할일·공유 보드)</li>
                  <li>예산노트</li>
                </ul>
              </>
            )}
            <div className="team-create-section">
              <p className="page-desc">파일 비밀번호 (선택 · 잊으면 파일을 열 수 없습니다)</p>
              <div className="form-row">
                <input
                  type="password"
                  autoComplete="new-password"
                  value={archivePassword}
                  onChange={(e) => setArchivePassword(e.target.value)}
                  placeholder="비워 두면 암호 없이 저장"
                  disabled={archiveBusy}
                />
              </div>
              {archivePassword ? (
                <div className="form-row">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={archivePassword2}
                    onChange={(e) => setArchivePassword2(e.target.value)}
                    placeholder="비밀번호 확인"
                    disabled={archiveBusy}
                  />
                </div>
              ) : null}
            </div>
            {archiveStatus ? <p className="inline-message">{archiveStatus}</p> : null}
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button type="button" className="cp-btn-secondary" disabled={archiveBusy} onClick={closeArchiveModal}>
                닫기
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                disabled={archiveBusy}
                onClick={() => void handleArchiveDownload()}
              >
                {archiveBusy ? '준비 중…' : '내려받기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ClassSettingsSection;
